import { SagaIterator } from 'redux-saga';
import { call } from 'redux-saga/effects';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Set } from '~commonInfra/utils/Set';
import { reloadSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { Formatters, Messages } from '../../codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '../../commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '../../commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '../../commonInfra/contentActions/MultipleItemResult';
import { openReactConfirmationDialog } from '../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { getFlowDraftId } from '../../drafts/utils/DraftUtils';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { DeleteFlowDraftReduxAction, FlowForDeleteDraft } from '../actions/DeleteFlowDraftReduxAction';
import { Flows } from '../resources/Flows';

export type DeleteFlowDraftsDialogActionParams = {
  readonly flows: ReadonlyArray<FlowForDeleteDraft>;
}

export function* handleDeleteFlowDraft(action: DeleteFlowDraftReduxAction): SagaIterator {
  if (action.flows && action.flows.length && action.flows[0].contentType === 'flow') {
    try {
      const flowsWithDrafts = action.flows.filter((flow) => {
        return getFlowDraftId(flow);
      });
      const noFlowDraftsSelected = flowsWithDrafts.length < 1;

      if (noFlowDraftsSelected) {
        const toaster = ToasterService.instance;
        toaster.pop({
          type: ToastType.Error,
          text: Messages.deleteFlowDraft_actionNoDraftsSelected()
        });
        return;
      }
      yield call(openDialog, flowsWithDrafts);
      yield call(reloadSpace);
    } catch(e) {
      // do nothing
    }
  }
}

function errorDetailFor(errorItem: IErrorItem<FlowForDeleteDraft>): IErrorDetail {
  const itemName = errorItem.item.name;
  let errorMessage: string;

  switch (errorItem.error.code) {
    // TODO update when BE adds appropriate error codes
    default:
      errorMessage = Messages.errorUnknown();
  }

  return { itemName, errorMessage };
}

function notifyResult(flows: Set<FlowForDeleteDraft>, result: IResult) {

  ActionHelpers.displayStandardToastsForResult(
    flows,
    result,
    {
      success: standardActionSuccessMsg(Messages.deleteFlowDraft_actionSuccess),
      error: standardActionErrorMsg(Messages.deleteFlowDraft_actionFailed),
    },
    (errorItem: IErrorItem<FlowForDeleteDraft>) => errorDetailFor(errorItem)
  );
}

export async function makeApiCall(props: DeleteFlowDraftsDialogActionParams): Promise<IResult> {
  const draftIds: string[] = props.flows.map((flow) => {
    return getFlowDraftId(flow);
  });

  const result = await Flows.deleteFlowDrafts(draftIds);

  return result;
}

function openDialog(flows: ReadonlyArray<FlowForDeleteDraft>) {
  const modalInstance = openReactConfirmationDialog<DeleteFlowDraftsDialogActionParams, IResult>({
    title: Messages.deleteFlowDraftDialogTitle(),
    message: Messages.confirmDeleteFlowDrafts({ COUNT: flows.length, COUNT_LOC: Formatters.formatCount(flows.length) }),
    actionName: Messages.confirmDeleteButton({ITEM_COUNT: flows.length, ITEM_COUNT_LOC: Formatters.formatCount(flows.length)}),
    action: makeApiCall,
    paramsForAction: { flows },
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });

  modalInstance.result.then((result: IResult) => {
    notifyResult(Set.fromIdItemArray(flows), result);
  });

  return modalInstance.result;
}
